import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Bell, ArrowLeft, Plus, Trash2, MapPin, Languages, Briefcase as BriefcaseIcon } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const AlertsPage = ({ user }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    keywords: '',
    location: '',
    spanish_level: '',
    sector: '',
  });

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/alerts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  };

  const createAlert = async (e) => {
    e.preventDefault();

    const keywordsArray = formData.keywords.split(',').map((k) => k.trim()).filter((k) => k);

    if (keywordsArray.length === 0) {
      toast.error('Agrega al menos una palabra clave');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          keywords: keywordsArray,
          location: formData.location || null,
          spanish_level: formData.spanish_level || null,
          sector: formData.sector || null,
        }),
      });

      if (response.ok) {
        toast.success('Alerta creada exitosamente');
        setFormData({ keywords: '', location: '', spanish_level: '', sector: '' });
        setShowForm(false);
        fetchAlerts();
      }
    } catch (error) {
      console.error('Error creating alert:', error);
      toast.error('Error al crear alerta');
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/alerts/${alertId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        setAlerts(alerts.filter((a) => a.id !== alertId));
        toast.success('Alerta eliminada');
      }
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Error al eliminar alerta');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Mis Alertas</h1>
            <p className="text-lg text-slate-600">Recibe notificaciones cuando aparezcan ofertas que te interesen</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} data-testid="create-alert-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Alerta
          </Button>
        </div>

        {/* Create Alert Form */}
        {showForm && (
          <Card className="mb-8 shadow-lg" data-testid="alert-form">
            <CardHeader>
              <CardTitle>Crear Nueva Alerta</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createAlert} className="space-y-4">
                <div>
                  <Label htmlFor="keywords">Palabras Clave (separadas por comas)</Label>
                  <Input
                    id="keywords"
                    placeholder="Ej: desarrollador, frontend, React"
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    required
                    data-testid="keywords-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="location">Ubicación (Opcional)</Label>
                    <Input
                      id="location"
                      placeholder="Ej: Zürich"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      data-testid="location-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="spanish-level">Nivel de Español (Opcional)</Label>
                    <Select value={formData.spanish_level} onValueChange={(value) => setFormData({ ...formData, spanish_level: value })}>
                      <SelectTrigger id="spanish-level" data-testid="spanish-level-select">
                        <SelectValue placeholder="Cualquiera" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="native">Nativo</SelectItem>
                        <SelectItem value="advanced">Avanzado</SelectItem>
                        <SelectItem value="intermediate">Intermedio</SelectItem>
                        <SelectItem value="basic">Básico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="sector">Sector (Opcional)</Label>
                    <Input
                      id="sector"
                      placeholder="Ej: Tecnología"
                      value={formData.sector}
                      onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                      data-testid="sector-input"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" data-testid="submit-alert-btn">Crear Alerta</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-alert-btn">
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Alerts List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando alertas...</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="alerts-list">
            {alerts.length === 0 ? (
              <Card className="p-12 text-center">
                <Bell className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold mb-2">No tienes alertas configuradas</h3>
                <p className="text-slate-600 mb-6">Crea una alerta para recibir notificaciones de nuevas ofertas</p>
                <Button onClick={() => setShowForm(true)} data-testid="create-first-alert-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Primera Alerta
                </Button>
              </Card>
            ) : (
              alerts.map((alert) => (
                <Card key={alert.id} className="hover:shadow-lg transition-all" data-testid={`alert-card-${alert.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-3">
                          <Bell className="w-5 h-5 text-red-600" />
                          <h3 className="text-xl font-bold">Alerta Activa</h3>
                          {alert.is_active && (
                            <Badge className="bg-green-100 text-green-700">Activa</Badge>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-start">
                            <span className="font-semibold mr-2 min-w-[120px]">Palabras clave:</span>
                            <div className="flex flex-wrap gap-1">
                              {alert.keywords.map((keyword, index) => (
                                <Badge key={index} variant="outline">
                                  {keyword}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          {alert.location && (
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-slate-500" />
                              <span className="text-slate-700">{alert.location}</span>
                            </div>
                          )}

                          {alert.spanish_level && (
                            <div className="flex items-center">
                              <Languages className="w-4 h-4 mr-2 text-slate-500" />
                              <span className="text-slate-700">Nivel: {alert.spanish_level}</span>
                            </div>
                          )}

                          {alert.sector && (
                            <div className="flex items-center">
                              <BriefcaseIcon className="w-4 h-4 mr-2 text-slate-500" />
                              <span className="text-slate-700">{alert.sector}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteAlert(alert.id)}
                        className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-alert-${alert.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPage;